import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { siteDiaryService } from '../../services/siteDiaryService';
import DiaryCreateForm from './components/DiaryCreateForm';
import DiaryListView from './components/DiaryListView';
import DiaryDeleteConfirmModal from './components/DiaryDeleteConfirmModal';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import Button from '../../components/ui/Button';

const SiteDiaryManagement = () => {
  const { userProfile } = useAuth();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('create');
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [company, setCompany] = useState(null);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [saving, setSaving] = useState(false);

  // NSherjFh PR 6: Delete + Restore + the 'View deleted' toggle are
  // admin-only. RLS would also permit site_managers / workers to
  // delete (admin_site_manager_full_access + workers_manage_own),
  // but the UI exposes the control to admins only — diary is the
  // operational/historical record and worker self-delete would muddy
  // the audit trail. Edit / markNothingToReport remains available to
  // workers via dailyRecordService.
  const isAdmin = userProfile?.role === 'admin';
  // NSherjFh PR 12: photo hard-delete gating is more permissive than
  // the entry-level soft-delete above. Photos are operational
  // artefacts ("storage hygiene" per Heidi's 28 May call), not the
  // audit record itself — so the gate mirrors actual RLS exactly:
  //   - admin_site_manager_full_access_diary_photos covers admin+SM
  //     for any photo in the company.
  //   - workers_manage_own_daily_photos covers uploaded_by =
  //     auth.uid() for the uploader themselves.
  // Today the only display surface (site-diary-management) is admin/SM
  // scoped, so the uploader branch is moot at the page level. Keeping
  // it future-proofs the gallery for any worker-facing surface added
  // later without a code change.
  const isAdminOrManager = ['admin', 'site_manager']?.includes(userProfile?.role);
  const canDeletePhoto = useCallback(
    (photo) => isAdminOrManager || photo?.uploaded_by === userProfile?.id,
    [isAdminOrManager, userProfile?.id]
  );
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const submittedByName = userProfile?.full_name || userProfile?.email || 'Current User';

  // Load company + sites on mount
  useEffect(() => {
    const init = async () => {
      setLoadingSites(true);
      try {
        const { data: companyData } = await companyService?.getUserCompany();
        setCompany(companyData);

        if (companyData?.id) {
          const { data: sitesData } = await siteService?.getAllSites();
          setSites(sitesData || []);
        }
      } catch (err) {
        console.error('Failed to load sites:', err);
      } finally {
        setLoadingSites(false);
      }
    };
    init();
  }, []);

  // Load entries when switching to list tab OR when toggling
  // between active and deleted views.
  const loadEntries = useCallback(async () => {
    if (!company?.id) return;
    setLoadingEntries(true);
    try {
      const { data, error } = await siteDiaryService?.getAllEntries(
        company?.id,
        { includeDeleted: viewDeleted }
      );
      if (error) throw error;
      // NSherjFh PR 6: deleted tab shows ONLY soft-deleted entries.
      // includeDeleted: true returns BOTH active and deleted; narrow
      // to the deleted ones for display.
      const rows = data || [];
      setEntries(viewDeleted ? rows?.filter((e) => e?.deleted_at) : rows);
    } catch (err) {
      toast?.error('Failed to load diary entries');
    } finally {
      setLoadingEntries(false);
    }
  }, [company?.id, viewDeleted]);

  useEffect(() => {
    if ((activeTab === 'list' || viewDeleted) && company?.id) {
      loadEntries();
    }
  }, [activeTab, viewDeleted, company?.id, loadEntries]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // NSherjFh PR 6 — soft-delete handlers. Mirrors PRs 3-5 flow.
  const handleDeleteClick = (entry) => {
    setDeletingEntry(entry);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEntry?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingEntry;
    try {
      const result = await siteDiaryService?.deleteEntry(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setEntries((prev) => prev?.filter((e) => e?.id !== target?.id));
      toast?.success('Diary entry moved to Deleted');
    } catch (err) {
      console.error('[SiteDiaryManagement] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete diary entry');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingEntry(null);
    }
  };

  // NSherjFh PR 12 — after DiaryPhotoGallery successfully hard-deletes
  // a photo + its storage file, refresh the page-owned entries state
  // so the gallery + counts re-render without the deleted row. Targets
  // exactly the parent entry; other entries are untouched (no full
  // network refetch).
  const handlePhotoDeleted = useCallback((photoId) => {
    setEntries((prev) =>
      prev?.map((e) => ({
        ...e,
        daily_site_photos: (e?.daily_site_photos || [])?.filter((p) => p?.id !== photoId),
      }))
    );
  }, []);

  const handleRestore = async (entry) => {
    try {
      const result = await siteDiaryService?.restoreEntry(entry?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setEntries((prev) => prev?.filter((e) => e?.id !== entry?.id));
      toast?.success('Diary entry restored');
    } catch (err) {
      console.error('[SiteDiaryManagement] restore failed:', err);
      // The most likely failure path here is the partial unique index
      // conflict — a new (site, user, date) entry was created while
      // the original was deleted. Surface the DB error verbatim so the
      // admin knows to soft-delete the conflicting newer entry first.
      toast?.error(err?.message || 'Failed to restore diary entry');
    }
  };

  const handleCreateEntry = async (formData, photoFiles) => {
    if (subscriptionReadOnly) return;
    if (!company?.id) {
      toast?.error('Company not found. Please refresh.');
      return;
    }
    setSaving(true);
    try {
      const { data: entry, error } = await siteDiaryService?.createEntry(formData, company?.id);
      if (error) throw error;

      // Upload photos if any
      if (photoFiles?.length > 0) {
        const uploadResults = await Promise.allSettled(
          photoFiles?.map(file => siteDiaryService?.uploadDiaryPhoto(file, entry?.id))
        );
        const failed = uploadResults?.filter(r => r?.status === 'rejected')?.length;
        if (failed > 0) {
          toast?.warning(`Entry saved, but ${failed} photo(s) failed to upload.`);
        }
      }

      toast?.success('Diary entry saved successfully!');
      setActiveTab('list');
    } catch (err) {
      console.error('[SiteDiaryManagement] save entry failed:', err);
      toast?.error('Failed to save diary entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Daily Site Diary | SiteProof</title>
      </Helmet>
      <Header />

      <main className="container mx-auto px-4 pt-24 pb-8 max-w-5xl">
        <SubscriptionReadOnlyBanner className="mb-6" />

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="BookOpen" size={20} className="text-accent" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                {viewDeleted ? 'Deleted diary entries' : t('dailySiteDiary')}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground ml-12">
              {viewDeleted
                ? 'Diary entries removed from active views. Restore to bring them back.'
                : t('dailySiteDiarySubtitle')}
            </p>
          </div>
          {/* NSherjFh PR 6: admin-only View Deleted toggle. */}
          {isAdmin && (
            <Button
              variant={viewDeleted ? 'default' : 'outline'}
              size="sm"
              iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
              onClick={() => { setActiveTab('list'); setViewDeleted((v) => !v); }}
            >
              {viewDeleted ? 'View active' : 'View deleted'}
            </Button>
          )}
        </div>

        {/* Tabs — hidden on the deleted tab (no Create on deleted) */}
        {!viewDeleted && (
          <div className="border-b border-border mb-6">
            <div className="flex gap-0">
              {[
                { id: 'create', label: t('createEntry'), icon: 'PlusCircle' },
                { id: 'list', label: t('viewEntries'), icon: 'List' },
              ]?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-6">
          {!viewDeleted && activeTab === 'create' && (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-foreground font-heading">{t('newDiaryEntry')}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{t('newDiaryEntrySubtitle')}</p>
              </div>
              {loadingSites ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 })?.map((_, i) => (
                    <div key={i} className="h-10 bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <DiaryCreateForm
                  sites={sites}
                  submittedByName={submittedByName}
                  onSubmit={handleCreateEntry}
                  loading={saving}
                  readOnly={subscriptionReadOnly}
                />
              )}
            </>
          )}

          {(viewDeleted || activeTab === 'list') && (
            <>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-semibold text-foreground font-heading">
                    {viewDeleted ? 'Deleted entries' : t('diaryEntries')}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {viewDeleted ? 'Restore to return an entry to the active list.' : t('diaryEntriesSubtitle')}
                  </p>
                </div>
                <button
                  onClick={loadEntries}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="RefreshCw" size={14} />
                  {t('refresh')}
                </button>
              </div>
              <DiaryListView
                entries={entries}
                sites={sites}
                loading={loadingEntries}
                onDelete={!viewDeleted && isAdmin ? handleDeleteClick : null}
                onRestore={viewDeleted && isAdmin ? handleRestore : null}
                canDeletePhoto={canDeletePhoto}
                onPhotoDeleted={handlePhotoDeleted}
              />
            </>
          )}
        </div>
      </main>
      <DiaryDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingEntry(null); }}
        onConfirm={handleConfirmDelete}
        entryLabel={
          deletingEntry
            ? `${deletingEntry?.sites?.site_name || 'site'} · ${deletingEntry?.record_date || ''}`
            : ''
        }
      />
    </div>
  );
};

export default SiteDiaryManagement;
